import type { TypographyVariant } from '@/components/atoms/Typography';
import type { DimensionValue } from 'react-native';

/**
 * Guest catalogue layout bands (web).
 * List columns follow card min-width so cards do not keep shrinking —
 * they step 4 → 3 → 2 → 1 as the viewport narrows.
 */
export const GUEST_PHONE_MAX = 768;
export const GUEST_TABLET_MAX = 1100;

/** Same side padding as properties-web list (`contentContainer`) — tablet band. */
export const GUEST_LIST_TABLET_PADDING_X = 24;
/** Guest has no WebAppLayout shell — phone uses 16px edge inset. */
export const GUEST_PHONE_PADDING_X = 16;
/** Same as properties-web details tablet (`contentScrollTablet`). */
export const GUEST_DETAILS_TABLET_PADDING_X = 8;
/** Same as properties-web desktop shell side padding (WebAppLayout `shell`) — list + details. */
export const GUEST_DESKTOP_PADDING_X = 32;

/**
 * Floor for a listing card column — do not shrink below Figma card width (~442).
 * Below this per column, drop one column count (4 → 3 → 2 → 1).
 */
export const GUEST_CARD_MIN_WIDTH = 442;
/** Match properties-web ListingCard / gridItem cap (and Figma ~442.67). */
export const GUEST_CARD_MAX_WIDTH = 443;

export type GuestListColumnCount = 1 | 2 | 3 | 4;

export interface GuestListLayout {
  readonly isPhone: boolean;
  readonly isTablet: boolean;
  readonly isDesktop: boolean;
  /** 1 | 2 | 3 | 4 — driven by content width vs card min width. */
  readonly columnCount: GuestListColumnCount;
  readonly columnWidth: DimensionValue;
  readonly paddingHorizontal: number;
}

export interface GuestDetailsLayout {
  readonly isPhone: boolean;
  readonly isTablet: boolean;
  /** Phone + tablet: Details | Explore tabs; desktop: side-by-side. */
  readonly isCompact: boolean;
  readonly carouselHeight: number;
  readonly paddingHorizontal: number;
}

export function getGuestColumnWidth(
  columnCount: GuestListColumnCount,
): DimensionValue {
  if (columnCount === 1) {
    return '100%';
  }
  if (columnCount === 2) {
    return '50%';
  }
  if (columnCount === 3) {
    return '33.333%';
  }
  return '25%';
}

function resolveListPaddingHorizontal(width: number): number {
  if (width < GUEST_PHONE_MAX) {
    return GUEST_PHONE_PADDING_X;
  }
  if (width < GUEST_TABLET_MAX) {
    return GUEST_LIST_TABLET_PADDING_X;
  }
  return GUEST_DESKTOP_PADDING_X;
}

/** Shared left/right inset for guest logo + title chrome (list + details). */
export function resolveGuestChromePaddingHorizontal(width: number): number {
  return resolveListPaddingHorizontal(width);
}

/**
 * Prefer more columns while each stays ≥ GUEST_CARD_MIN_WIDTH.
 * Steps: 4 → 3 → 2 → 1.
 */
function resolveListColumnCount(contentWidth: number): GuestListColumnCount {
  if (contentWidth >= GUEST_CARD_MIN_WIDTH * 4) {
    return 4;
  }
  if (contentWidth >= GUEST_CARD_MIN_WIDTH * 3) {
    return 3;
  }
  if (contentWidth >= GUEST_CARD_MIN_WIDTH * 2) {
    return 2;
  }
  return 1;
}

export function getGuestListLayout(width: number): GuestListLayout {
  const paddingHorizontal = resolveListPaddingHorizontal(width);
  const contentWidth = Math.max(0, width - paddingHorizontal * 2);
  const columnCount = resolveListColumnCount(contentWidth);

  return {
    isPhone: columnCount === 1,
    isTablet: columnCount === 2 || columnCount === 3,
    isDesktop: columnCount === 4,
    columnCount,
    columnWidth: getGuestColumnWidth(columnCount),
    paddingHorizontal,
  };
}

function resolveCarouselHeight(isPhone: boolean, isTablet: boolean): number {
  if (isPhone) {
    return 228;
  }
  if (isTablet) {
    return 320;
  }
  return 421;
}

export function getGuestDetailsLayout(width: number): GuestDetailsLayout {
  const isPhone = width < GUEST_PHONE_MAX;
  const isTablet = width >= GUEST_PHONE_MAX && width < GUEST_TABLET_MAX;

  let paddingHorizontal = GUEST_DESKTOP_PADDING_X;
  if (isPhone) {
    paddingHorizontal = GUEST_PHONE_PADDING_X;
  } else if (isTablet) {
    paddingHorizontal = GUEST_DETAILS_TABLET_PADDING_X;
  }

  return {
    isPhone,
    isTablet,
    isCompact: isPhone || isTablet,
    carouselHeight: resolveCarouselHeight(isPhone, isTablet),
    paddingHorizontal,
  };
}

/**
 * Details page type scale by viewport.
 * Phone mirrors listing-card sizes (title3 / caption / subhead) so hero
 * text doesn’t overpower the explore cards on the same screen.
 */
export interface GuestDetailsTypeScale {
  readonly pageTitle: TypographyVariant;
  readonly location: TypographyVariant;
  readonly description: TypographyVariant;
  readonly aboutHeading: TypographyVariant;
  readonly aboutValue: TypographyVariant;
  readonly aboutTag: TypographyVariant;
  readonly amenityLabel: TypographyVariant;
  readonly accordionTitle: TypographyVariant;
  readonly mapIconSize: number;
  readonly locationLineHeight: number;
  readonly descriptionLineHeight: number;
  /** Floating Login CTA + login prompt dialog. */
  readonly loginButtonLabel: TypographyVariant;
  readonly loginButtonLineHeight: number;
  readonly loginButtonHeight: number;
  readonly loginDialogTitle: TypographyVariant;
  readonly loginDialogSubtitle: TypographyVariant;
  readonly loginDialogTitleLineHeight: number;
  readonly loginDialogIconSize: number;
  readonly loginDialogPadding: number;
  readonly loginDialogGap: number;
  readonly loginActionFontSize: number;
  readonly loginActionLineHeight: number;
}

export function getGuestDetailsTypeScale(
  layout: Pick<GuestDetailsLayout, 'isPhone' | 'isTablet'>,
): GuestDetailsTypeScale {
  if (layout.isPhone) {
    return {
      pageTitle: 'title3',
      location: 'caption',
      description: 'body',
      aboutHeading: 'subhead',
      aboutValue: 'headline',
      aboutTag: 'subhead',
      amenityLabel: 'caption',
      accordionTitle: 'headline',
      mapIconSize: 20,
      locationLineHeight: 18,
      descriptionLineHeight: 22,
      loginButtonLabel: 'title3',
      loginButtonLineHeight: 20,
      loginButtonHeight: 48,
      loginDialogTitle: 'title3',
      loginDialogSubtitle: 'caption',
      loginDialogTitleLineHeight: 20,
      loginDialogIconSize: 56,
      loginDialogPadding: 16,
      loginDialogGap: 12,
      loginActionFontSize: 14,
      /** > fontSize so Arabic descenders/dots aren’t clipped in Button. */
      loginActionLineHeight: 20,
    };
  }
  if (layout.isTablet) {
    return {
      pageTitle: 'title2',
      location: 'subhead',
      description: 'body',
      aboutHeading: 'headline',
      aboutValue: 'title3',
      aboutTag: 'subhead',
      amenityLabel: 'subhead',
      accordionTitle: 'headlineLarge',
      mapIconSize: 22,
      locationLineHeight: 20,
      descriptionLineHeight: 22,
      loginButtonLabel: 'title2',
      loginButtonLineHeight: 22,
      loginButtonHeight: 52,
      loginDialogTitle: 'title2',
      loginDialogSubtitle: 'subhead',
      loginDialogTitleLineHeight: 22,
      loginDialogIconSize: 64,
      loginDialogPadding: 20,
      loginDialogGap: 16,
      loginActionFontSize: 15,
      loginActionLineHeight: 20,
    };
  }
  return {
    pageTitle: 'title1',
    location: 'bodyLarge',
    description: 'bodyLarge',
    aboutHeading: 'title3',
    aboutValue: 'title3',
    aboutTag: 'subhead',
    amenityLabel: 'body',
    accordionTitle: 'title3',
    mapIconSize: 24,
    locationLineHeight: 22,
    descriptionLineHeight: 24,
    loginButtonLabel: 'title1',
    loginButtonLineHeight: 28,
    loginButtonHeight: 56,
    loginDialogTitle: 'title1',
    loginDialogSubtitle: 'body',
    loginDialogTitleLineHeight: 28,
    loginDialogIconSize: 80,
    loginDialogPadding: 24,
    loginDialogGap: 24,
    loginActionFontSize: 16,
    loginActionLineHeight: 20,
  };
}

/** Initial skeleton cards while guest list API is pending (2 rows × 4 cols). */
export const GUEST_LIST_SKELETON_COUNT = 8;
